import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { FaHistory, FaTrash } from 'react-icons/fa';
// @ts-ignore
import { Helmet } from 'react-helmet';

interface CalculationHistory {
  expression: string;
  result: string;
  timestamp: Date;
}

const Calculator: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [display, setDisplay] = useState<string>('0');
  const [expression, setExpression] = useState<string>('');
  const [memory, setMemory] = useState<number>(0);
  const [angleMode, setAngleMode] = useState<'DEG' | 'RAD'>('DEG');
  const [history, setHistory] = useState<CalculationHistory[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<number | null>(null);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const key = event.key;
      
      // Numbers and decimal
      if (/^[0-9.]$/.test(key)) {
        if (key === '.') {
          inputDecimal();
        } else {
          inputDigit(key);
        }
      }
      
      // Operators
      if (['+', '-', '*', '/', '^'].includes(key)) {
        handleOperator(key === '^' ? '**' : key);
      }
      
      // Enter or = for calculation
      if (key === 'Enter' || key === '=') {
        calculate();
      }
      
      // Escape for clear
      if (key === 'Escape') {
        clearDisplay();
      }
      
      // Backspace for delete last digit
      if (key === 'Backspace') {
        deleteLastDigit();
      }

      // Special keys
      if (key === 'p') {
        handleFunction('pi');
      } else if (key === 'e') {
        handleFunction('e');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [display, expression]);

  const inputDigit = (digit: string) => {
    if (display === '0' || display === 'Error') {
      setDisplay(digit);
    } else {
      setDisplay(display + digit);
    }
  };

  const inputDecimal = () => {
    if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const deleteLastDigit = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const clearDisplay = () => {
    setDisplay('0');
    setExpression('');
    setLastResult(null);
  };

  const handleOperator = (op: string) => {
    const currentValue = parseFloat(display);
    if (lastResult !== null) {
      setExpression(`${lastResult} ${op}`);
      setLastResult(null);
    } else {
      setExpression(expression ? `${expression} ${currentValue} ${op}` : `${currentValue} ${op}`);
    }
    setDisplay('0');
  };

  const calculate = () => {
    try {
      const currentValue = parseFloat(display);
      const fullExpression = expression ? `${expression} ${currentValue}` : currentValue.toString();
      const result = eval(fullExpression);
      setDisplay(result.toString());
      setLastResult(result);
      addToHistory(fullExpression, result.toString());
      setExpression('');
    } catch (error) {
      setDisplay('Error');
    }
  };

  const addToHistory = (expression: string, result: string) => {
    setHistory(prev => [{
      expression,
      result,
      timestamp: new Date()
    }, ...prev].slice(0, 20)); // Keep last 20 calculations
  };

  const clearHistory = () => {
    setHistory([]);
  };

  const handleHistorySelect = (expression: string, result: string) => {
    setDisplay(result);
    setExpression(expression);
  };

  const memoryOperation = (operation: string) => {
    const value = parseFloat(display);
    switch (operation) {
      case 'M+':
        setMemory(memory + value);
        break;
      case 'M-':
        setMemory(memory - value);
        break;
      case 'MR':
        setDisplay(memory.toString());
        setExpression(memory.toString());
        break;
      case 'MC':
        setMemory(0);
        break;
    }
  };

  const handleFunction = (func: string) => {
    let newExpression = '';
    const currentValue = parseFloat(display);

    switch (func) {
      case 'sin':
        newExpression = `Math.sin(${angleMode === 'DEG' ? 'Math.PI/180*' : ''}${currentValue})`;
        break;
      case 'cos':
        newExpression = `Math.cos(${angleMode === 'DEG' ? 'Math.PI/180*' : ''}${currentValue})`;
        break;
      case 'tan':
        newExpression = `Math.tan(${angleMode === 'DEG' ? 'Math.PI/180*' : ''}${currentValue})`;
        break;
      case 'asin':
        newExpression = `Math.asin(${currentValue})${angleMode === 'DEG' ? '*180/Math.PI' : ''}`;
        break;
      case 'acos':
        newExpression = `Math.acos(${currentValue})${angleMode === 'DEG' ? '*180/Math.PI' : ''}`;
        break;
      case 'atan':
        newExpression = `Math.atan(${currentValue})${angleMode === 'DEG' ? '*180/Math.PI' : ''}`;
        break;
      case 'log':
        newExpression = `Math.log10(${currentValue})`;
        break;
      case 'ln':
        newExpression = `Math.log(${currentValue})`;
        break;
      case 'sqrt':
        newExpression = `Math.sqrt(${currentValue})`;
        break;
      case 'exp':
        newExpression = `Math.exp(${currentValue})`;
        break;
      case 'x2':
        newExpression = `Math.pow(${currentValue}, 2)`;
        break;
      case 'x3':
        newExpression = `Math.pow(${currentValue}, 3)`;
        break;
      case 'pi':
        newExpression = 'Math.PI';
        break;
      case 'e':
        newExpression = 'Math.E';
        break;
      case 'abs':
        newExpression = `Math.abs(${currentValue})`;
        break;
      case 'factorial':
        if (currentValue < 0 || !Number.isInteger(currentValue)) {
          setDisplay('Error');
          return;
        }
        let result = 1;
        for (let i = 2; i <= currentValue; i++) {
          result *= i;
        }
        setDisplay(result.toString());
        addToHistory(`${currentValue}!`, result.toString());
        return;
      case '1/x':
        newExpression = `1/${currentValue}`;
        break;
      case '10x':
        newExpression = `Math.pow(10, ${currentValue})`;
        break;
      case 'rand':
        newExpression = 'Math.random()';
        break;
      case 'floor':
        newExpression = `Math.floor(${currentValue})`;
        break;
      case 'ceil':
        newExpression = `Math.ceil(${currentValue})`;
        break;
      case 'round':
        newExpression = `Math.round(${currentValue})`;
        break;
    }

    try {
      const result = eval(newExpression);
      setDisplay(result.toString());
      addToHistory(newExpression, result.toString());
    } catch (error) {
      setDisplay('Error');
    }
  };

  return (
    <>
      <Helmet>
        <title>Calculator | Basic Arithmetic Online - Davinqi</title>
        <meta name="description" content="Perform basic arithmetic operations online with Davinqi Calculator. Fast, accurate, and privacy-friendly." />
        <meta property="og:title" content="Calculator | Basic Arithmetic Online - Davinqi" />
        <meta property="og:description" content="Perform basic arithmetic operations online with Davinqi Calculator. Fast, accurate, and privacy-friendly." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/math/calculator" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Calculator | Basic Arithmetic Online - Davinqi" />
        <meta name="twitter:description" content="Perform basic arithmetic operations online with Davinqi Calculator. Fast, accurate, and privacy-friendly." />
      </Helmet>
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-4 transition-all duration-300 ease-in-out">
          <div className="text-gray-500 dark:text-gray-400 text-sm min-h-[20px] font-mono">
            {expression}
          </div>
          <div className="text-gray-900 dark:text-white text-4xl font-light min-h-[48px] flex items-center justify-end font-mono">
            {display}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => setAngleMode(angleMode === 'DEG' ? 'RAD' : 'DEG')}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {angleMode}
            </button>
            <div className="flex space-x-2">
              {['MC', 'MR', 'M+', 'M-'].map((op) => (
                <button
                  key={op}
                  onClick={() => memoryOperation(op)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {op}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-6 gap-3">
            {/* Scientific functions */}
            {[
              { label: 'sin', func: 'sin' },
              { label: 'cos', func: 'cos' },
              { label: 'tan', func: 'tan' },
              { label: 'log', func: 'log' },
              { label: 'ln', func: 'ln' },
              { label: '√', func: 'sqrt' },
              { label: 'sin⁻¹', func: 'asin' },
              { label: 'cos⁻¹', func: 'acos' },
              { label: 'tan⁻¹', func: 'atan' },
              { label: 'eˣ', func: 'exp' },
              { label: 'x²', func: 'x2' },
              { label: 'x³', func: 'x3' },
              { label: 'π', func: 'pi' },
              { label: 'e', func: 'e' },
              { label: '|x|', func: 'abs' },
              { label: 'x!', func: 'factorial' },
              { label: '1/x', func: '1/x' },
              { label: '10ˣ', func: '10x' },
              { label: 'rand', func: 'rand' },
              { label: '⌊x⌋', func: 'floor' },
              { label: '⌈x⌉', func: 'ceil' },
              { label: 'round', func: 'round' }
            ].map(({ label, func }) => (
              <button
                key={func}
                onClick={() => handleFunction(func)}
                className="p-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-3">
            <button
              className="col-span-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-4 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={clearDisplay}
            >
              AC
            </button>
            <button
              className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-4 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={() => deleteLastDigit()}
            >
              ⌫
            </button>
            <button
              className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-4 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              onClick={() => handleOperator('/')}
            >
              ÷
            </button>

            {[7, 8, 9, 4, 5, 6, 1, 2, 3, 0, '.'].map((digit) => (
              <button
                key={digit}
                className={`bg-white dark:bg-gray-800 text-gray-900 dark:text-white p-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  digit === 0 ? 'col-span-2' : ''
                }`}
                onClick={() => digit === '.' ? inputDecimal() : inputDigit(digit.toString())}
              >
                {digit}
              </button>
            ))}

            {['*', '-', '+', '='].map((op) => (
              <button
                key={op}
                className={`p-4 rounded-xl transition-colors ${
                  op === '='
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                onClick={() => op === '=' ? calculate() : handleOperator(op)}
              >
                {op === '*' ? '×' : op}
              </button>
            ))}
          </div>
        </div>

        <div className="fixed bottom-4 right-4 flex space-x-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg"
          >
            {/* @ts-ignore */}
            <FaHistory />
          </button>
        </div>

        {showHistory && (
          <div className="fixed bottom-20 right-4 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 transition-all duration-300 ease-in-out">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">History</h3>
              <button
                onClick={clearHistory}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {/* @ts-ignore */}
                <FaTrash />
              </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleHistorySelect(item.expression, item.result)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="text-sm text-gray-500 dark:text-gray-400">{item.expression}</div>
                  <div className="text-gray-900 dark:text-white">{item.result}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Calculator; 